import { useEffect, useRef } from 'react'

/**
 * usePollWhileVisible
 *
 * Runs a callback on a fixed interval while the tab is visible, skipping ticks while the tab is
 * hidden so background tabs don't poll. When the tab regains focus after being hidden long enough
 * to have missed a tick, it runs once immediately instead of waiting for the next interval.
 *
 * @param onTick - The function to run on each visible tick
 * @param intervalMs - The polling interval in milliseconds
 * @param enabled - When false, no interval runs (defaults to true)
 */
export function usePollWhileVisible(onTick: () => void, intervalMs: number, enabled: boolean = true) {
  // Call the latest onTick via a ref so a new inline function each render doesn't restart the interval.
  const onTickRef = useRef(onTick)
  onTickRef.current = onTick

  useEffect(() => {
    if (!enabled) return

    let lastRun = Date.now()
    // Run only when visible and at least an interval has passed, so a quick tab flip doesn't
    // refetch and the on-focus catch-up can't double up with a scheduled tick.
    const runIfDue = () => {
      if (document.hidden || Date.now() - lastRun < intervalMs) return
      lastRun = Date.now()
      onTickRef.current()
    }

    const intervalId = setInterval(runIfDue, intervalMs)
    document.addEventListener('visibilitychange', runIfDue)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', runIfDue)
    }
  }, [enabled, intervalMs])
}
